import React, { useState, useEffect } from 'react';
import { 
    Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, 
    TableRow, TextField, IconButton, CircularProgress, Alert, Button, Typography 
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckIcon from '@mui/icons-material/Check';
import { useHttpClient, useAlert } from '../../../modules/services/Bundle'; 
import { RequestMethod } from '../../../modules/services/core/_enums';
import './TableColumnConfig.css'; 

interface TableColumnConfigProps {
    corporationName: string;
}

interface ColumnConfigItem {
    ColunaPadraoId: number;
    Field: string;
    OriginalTitle: string;
    Title: string;
}

const AVAILABLE_SCREENS = [
    { code: 'DASHBOARD', label: 'Inicial - Dashboard' },
    { code: 'TABELA_GATEWAYS', label: 'Tabela de Gateways' },
    { code: 'TABELA_ATIVOS', label: 'Tabela de Ativos'},
    { code: 'MOVIMENTACOES', label: 'Movimentações'},
    { code: 'ASSOCIACAO_ATIVOS', label: 'Associação de Ativos'},
    { code: 'ADICIONAR_ASSOCIACAO', label: 'Adicionar Associação de Ativos'},
    { code: 'HISTORICO_ASSOCIACAO', label: 'Histórico de Associações'},
    { code: 'VISAO_GERAL', label: 'Visão Geral'},
    { code: 'MAPA_LOCAIS', label: 'Mapa de Locais'},
];

const TableColumnConfig: React.FC<TableColumnConfigProps> = ({ corporationName }) => {
    const httpClient = useHttpClient();
    const alert = useAlert();
    
    const [selectedScreen, setSelectedScreen] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [colunas, setColunas] = useState<ColumnConfigItem[]>([]);

    const fetchColumns = async (screenCode: string) => {
        if (!screenCode) return;
        setLoading(true);
        try {
            const response = await httpClient.request({
                method: RequestMethod.GET,
                endpoint: `/api/labels/${screenCode}/${corporationName}`
            });

            if (response.success) {
                const data = response.payload as ColumnConfigItem[];
                setColunas(data);
            } else {
                throw new Error('Falha ao carregar dados');
            }
        } catch (error) {
            console.error(error);
            alert.createMessage({ type: 'error', message: 'Erro ao carregar colunas.' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedScreen) {
            fetchColumns(selectedScreen);
        }
    }, [selectedScreen]);

    const handleSaveLine = async (colunaId: number, novoTitulo: string) => {
        try {
            const response = await httpClient.request({
                method: RequestMethod.POST,
                endpoint: '/api/labels/configurar',
                data: {
                    Corporacao: corporationName,
                    ColunaPadraoId: colunaId,
                    NovoTitulo: novoTitulo
                }
            });

            if (response.success || (response as any).IsRequestSuccessful) {
                alert.createMessage({ icon: 'success', message: 'Rótulo atualizado!' });
            } else {
                throw new Error('Falha ao salvar');
            }
        } catch (error) {
            alert.createMessage({ type: 'error', message: 'Erro ao salvar alteração.' });
        }
    };

    const handleTitleChange = (id: number, newValue: string) => {
        setColunas(prev => prev.map(item => 
            item.ColunaPadraoId === id ? { ...item, Title: newValue } : item
        ));
    };

    return (
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', minHeight: '500px' }}>
            
            {/* --- LISTA LATERAL --- */}
            <div className="screen-selector-container">
                <span className="screen-selector-label">Selecione a Tela</span>
                
                <div className="screen-list-wrapper">
                    <div className="screen-list-header">Telas Disponíveis</div>
                    <ul className="screen-list">
                        {AVAILABLE_SCREENS.map((screen) => (
                            <li 
                                key={screen.code} 
                                className={`screen-list-item ${selectedScreen === screen.code ? 'selected' : ''}`}
                                onClick={() => setSelectedScreen(screen.code)}
                            >
                                <span>{screen.label}</span>
                                {selectedScreen === screen.code && <CheckIcon className="check-icon" />}
                            </li>
                        ))}
                    </ul>
                </div>
                
                {selectedScreen && (
                    <Button 
                        startIcon={<RefreshIcon />} 
                        onClick={() => fetchColumns(selectedScreen)}
                        variant="outlined"
                        fullWidth
                        sx={{ mt: 1, textTransform: 'none' }}
                    >
                        Recarregar Dados
                    </Button>
                )}
            </div>

            {/* --- ÁREA DA TABELA --- */}
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <span className="screen-selector-label">
                    {selectedScreen ? 'Configuração de Campos' : ' '}
                </span>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 8, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && selectedScreen && colunas.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>Nenhuma coluna configurável encontrada para esta tela.</Alert>
                )}
                
                {!selectedScreen && !loading && (
                    <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary', bgcolor: 'transparent', boxShadow: 'none', border: '1px dashed #ccc' }}>
                        <Typography>Selecione uma tela na lista ao lado para começar a editar.</Typography>
                    </Paper>
                )}

                {!loading && colunas.length > 0 && (
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="35%"><strong>Título Original</strong></TableCell>
                                    <TableCell width="50%"><strong>Título Personalizado (Editável)</strong></TableCell>
                                    <TableCell align="center" width="15%"><strong>Ação</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {colunas.map((item) => (
                                    <TableRow key={item.ColunaPadraoId} hover>
                                        <TableCell sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                                            {item.OriginalTitle}
                                            {/* Opcional: mostrar chave técnica pequena */}
                                            {/* <Typography variant="caption" display="block" color="text.disabled">{item.Field}</Typography> */}
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                value={item.Title}
                                                onChange={(e) => handleTitleChange(item.ColunaPadraoId, e.target.value)}
                                                sx={{ backgroundColor: 'transparent' }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton 
                                                color="primary" 
                                                onClick={() => handleSaveLine(item.ColunaPadraoId, item.Title)}
                                                title="Salvar alteração"
                                                size="small"
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </Box>
    );
};

export default TableColumnConfig;